import i18n from '@config/i18n/config';
import { apiClient } from '@lib/api-client';
import {
	queryClient,
	TMutationConfig,
} from '@lib/react-query';
import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';

export interface IBrandResponse {
	id: string | number;
	name: string;
}

interface ICreateBrandPayload {
	organization: string;
	name: string;
	location: {
		countryCode: string;
		place: string;
	};
	legalIdentifier?: string;
	contact?: string | null;
	offsetPolicy?: 'VOLUNTARY' | 'MANDATORY';
	obfuscateIPFS?: boolean;
}

type TUseCreateBrandOptions = {
	mutationConfig?: TMutationConfig<typeof createBrand>;
};

export const createBrand = ({
	body,
}: {
	body: ICreateBrandPayload;
}): Promise<IBrandResponse> => {
	return apiClient.post('/brands', body);
};

export const useCreateBrand = ({
	mutationConfig,
}: TUseCreateBrandOptions = {}) => {
	const { onSuccess, onError, ...restConfig } =
		mutationConfig || {};

	return useMutation({
		onSuccess: (...args) => {
			queryClient.invalidateQueries({
				queryKey: ['brands'],
			});
			queryClient.invalidateQueries({
				queryKey: ['organizations'],
			});

			notifications.show({
				title: i18n.t('global.success'),
				message: i18n.t('global.brandCreatedSuccess'),
				color: 'green',
			});
			onSuccess?.(...args);
		},
		onError: (...args) => {
			notifications.show({
				title: i18n.t('global.error'),
				message: i18n.t('global.somethingWentWrong'),
				color: 'var(--mantine-color-igluRed-9)',
			});
			onError?.(...args);
		},
		mutationFn: createBrand,
		...restConfig,
	});
};
